import React from "react";
import {
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonGrid,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonNote,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import { US_REGION_NAMES } from "../../../constants/usRegions";
import type {
    NewAgentLicenseForm,
    SettingsStateOption,
    Tier2UserOption,
} from "../AcaSettingsTypes";

const ALL_STATES: SettingsStateOption[] = Object.entries(US_REGION_NAMES).map(([code, name]) => ({code, name,}));

interface ModalAddTier2AgentProps {
    modal: {
        isOpen: boolean;
        onCancel: () => void;
    };
    data: {
        tier2UserOptions: Tier2UserOption[];
        newAgentLicenses: NewAgentLicenseForm[];
    };
    form: {
        tier2NewUserId: number | null;
        setTier2NewUserId: (value: number | null) => void;
        tier2NewNpn: string;
        setTier2NewNpn: (value: string) => void;
        tier2NewNpnError: string;
    };
    actions: {
        updateNewAgentLicenseRow: (
            rowId: number,
            patch: Partial<Omit<NewAgentLicenseForm, "id">>
        ) => void;
        removeNewAgentLicenseRow: (rowId: number) => void;
        addNewAgentLicenseRow: () => void;
        onSave: () => void | Promise<void>;
    };
}

const ModalAddTier2Agent: React.FC<ModalAddTier2AgentProps> = ({ modal, data, form, actions }) => {
    const { isOpen, onCancel } = modal;
    const { tier2UserOptions, newAgentLicenses } = data;
    const {tier2NewUserId, setTier2NewUserId, tier2NewNpn, setTier2NewNpn, tier2NewNpnError,} = form;
    const {updateNewAgentLicenseRow, removeNewAgentLicenseRow, addNewAgentLicenseRow, onSave,} = actions;

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onCancel}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Add Tier 2 Agent</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onCancel}>Cancel</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <IonList>
                    <IonItem>
                        <IonSelect
                            label="Tier 2 Users"
                            labelPlacement="floating"
                            interface="popover"
                            value={tier2NewUserId}
                            onIonChange={(e) => {
                                setTier2NewUserId((e.detail.value as number) ?? null);
                            }}
                        >
                            {tier2UserOptions.map((user) => (
                                <IonSelectOption key={user.id} value={user.id}>
                                    {user.displayName} ({user.email})
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonItem>

                    <IonItem>
                        <IonInput
                            label="NPN Number"
                            labelPlacement="floating"
                            value={tier2NewNpn}
                            onIonInput={(e) => setTier2NewNpn((e.detail.value ?? "").toString())}
                            placeholder="Enter NPN"
                        />
                    </IonItem>
                    {tier2NewNpnError && (
                        <IonText color="danger">
                            <p style={{ marginTop: 6, marginBottom: 0 }}>{tier2NewNpnError}</p>
                        </IonText>
                    )}

                    <IonItem lines="none">
                        <IonNote color="medium" className="ion-text-wrap">
                            New agents are created as <strong>Active</strong> by default. You can later set
                            them inactive from the agent tile.
                        </IonNote>
                    </IonItem>
                </IonList>

                <IonText>
                    <h3 style={{ marginTop: 16, marginBottom: 8 }}>Add Licenses (optional)</h3>
                </IonText>

                {newAgentLicenses.map((license) => (
                    <IonGrid key={license.id} style={{ marginBottom: 12 }}>
                        <IonRow>
                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">State</IonLabel>
                                    <IonSelect
                                        value={license.stateCode}
                                        placeholder="Select state"
                                        interface="popover"
                                        onIonChange={(e) =>
                                            updateNewAgentLicenseRow(license.id, {
                                                stateCode: (e.detail.value || "").toString(),
                                            })
                                        }
                                    >
                                        {ALL_STATES.map((state) => (
                                            <IonSelectOption key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonCol>

                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">License Number</IonLabel>
                                    <IonInput
                                        value={license.licenseNumber}
                                        placeholder="Enter license number"
                                        onIonChange={(e) =>
                                            updateNewAgentLicenseRow(license.id, {
                                                licenseNumber: (e.detail.value || "").toString(),
                                            })
                                        }
                                    />
                                </IonItem>
                            </IonCol>

                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">Expiration Date</IonLabel>
                                    <IonInput
                                        type="date"
                                        value={license.expirationDate}
                                        onIonChange={(e) =>
                                            updateNewAgentLicenseRow(license.id, {
                                                expirationDate: (e.detail.value || "").toString(),
                                            })
                                        }
                                    />
                                </IonItem>
                            </IonCol>
                        </IonRow>

                        <IonRow>
                            <IonCol size="12" className="ion-text-right ion-align-self-center">
                                {newAgentLicenses.length > 1 && (
                                    <IonButton
                                        fill="clear"
                                        color="danger"
                                        onClick={() => removeNewAgentLicenseRow(license.id)}
                                    >
                                        Remove
                                    </IonButton>
                                )}
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                ))}

                <IonButton
                    fill="outline"
                    size="small"
                    onClick={addNewAgentLicenseRow}
                    style={{ marginTop: 4, marginBottom: 16 }}
                >
                    Add Another License
                </IonButton>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 16,
                        gap: 8,
                    }}
                >
                    <IonButton fill="outline" onClick={onCancel}>
                        Cancel
                    </IonButton>
                    <IonButton onClick={onSave} disabled={!tier2NewUserId || !tier2NewNpn || !!tier2NewNpnError}>
                        Save
                    </IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};

export default ModalAddTier2Agent;